import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { RecordTimeline, type RecordTimelineEvent } from "@/components/crm/RecordTimeline";

const crmModules = [
  { id: "leads", label: "Leads" },
  { id: "contacts", label: "Contacts" },
  { id: "accounts", label: "Accounts" },
  { id: "deals", label: "Deals" },
  { id: "automation", label: "Automation" },
];

export function CrmAuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(query);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<RecordTimelineEvent[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const qParam = query ? `&q=${encodeURIComponent(query)}` : "";
      const res = await apiGet<{ events?: RecordTimelineEvent[] }>(`/api/audit/app/crm?limit=400${qParam}`);
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load CRM audit history.");
        setEvents([]);
      } else {
        setError(null);
        setEvents(res.data?.events ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">CRM activity history</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">CRM audit trail for this organization only.</p>
        </div>
        <Link
          to="/settings/audit"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Organization view
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        {crmModules.map((module) => (
          <Link
            key={module.id}
            to={`/crm/audit/${module.id}`}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
          >
            {module.label}
          </Link>
        ))}
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Filter audit events"
          className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <button type="submit" className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20">
          Filter
        </button>
      </form>

      {loading ? <p className="text-sm text-slate-500">Loading audit events…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      {!loading && !error ? (
        <RecordTimeline events={events} emptyMessage="No CRM audit events matched your filter." />
      ) : null}
    </div>
  );
}

export function CrmAuditModulePage() {
  const { moduleId = "" } = useParams<{ moduleId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<RecordTimelineEvent[]>([]);

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<{ events?: RecordTimelineEvent[] }>(
        `/api/audit/app/crm/module/${encodeURIComponent(moduleId)}?limit=400`,
      );
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load module history.");
        setEvents([]);
      } else {
        setError(null);
        setEvents(res.data?.events ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  const label = crmModules.find((m) => m.id === moduleId)?.label || moduleId;

  return (
    <div className="space-y-4">
      <Link to="/crm/audit" className="text-sm text-indigo-600 hover:underline">
        ← CRM activity history
      </Link>
      <h1 className="text-2xl font-semibold">{label} history</h1>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      {!loading && !error ? (
        <RecordTimeline events={events} emptyMessage={`No audit events for ${label.toLowerCase()}.`} />
      ) : null}
    </div>
  );
}

export function CrmAuditRecordPage() {
  const { moduleId = "", recordId = "" } = useParams<{ moduleId: string; recordId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<RecordTimelineEvent[]>([]);

  useEffect(() => {
    if (!moduleId || !recordId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<{ events?: RecordTimelineEvent[] }>(
        `/api/audit/app/crm/module/${encodeURIComponent(moduleId)}/record/${encodeURIComponent(recordId)}?limit=200`,
      );
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load record history.");
        setEvents([]);
      } else {
        setError(null);
        setEvents(res.data?.events ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, recordId]);

  return (
    <div className="space-y-4">
      <Link to={`/crm/audit/${encodeURIComponent(moduleId)}`} className="text-sm text-indigo-600 hover:underline">
        ← Module history
      </Link>
      <h1 className="text-2xl font-semibold">Record history</h1>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {moduleId} · {recordId}
      </p>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      {!loading && !error ? (
        <RecordTimeline events={events} emptyMessage="No audit events for this record." />
      ) : null}
    </div>
  );
}
